import Phaser from 'phaser';
import type { CrittersPreset, CritterTypeConfig } from '@/config/critters.config';
import { AmbientCritter } from '@/game/entities/AmbientCritter';

/**
 * AmbientCrittersSystem — manager dekoracyjnych stworzonek tła (motyle, ptaki,
 * biedronki…). Czwarty system wizualny per-biom (po ambientFx/atmosphere/parallax).
 *
 * Per typ stworzonka trzyma scheduler: co `spawnIntervalMs` spawnuje nowe
 * stworzonko, o ile nie przekroczono `maxConcurrent`. Każde stworzonko
 * (`AmbientCritter`) samo zarządza ścieżką i zgłasza `done` po wyjściu z kadru /
 * upływie życia — wtedy manager je niszczy.
 *
 * **Fail-soft:** typ z brakującą teksturą jest pomijany z `warn` (gra działa).
 *
 * **Cykl życia:** `update(time,dt)` wołany z `WorldScene.update`; `destroy()` z
 * `WorldScene.cleanup()` + reset block (zombie-state pattern jak ambientFx/
 * atmosphere/parallax). Idempotentny.
 */
interface TypeState {
  cfg: CritterTypeConfig;
  nextSpawnAt: number;
  alive: AmbientCritter[];
}

export class AmbientCrittersSystem {
  private readonly types: TypeState[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    preset: CrittersPreset,
  ) {
    const now = scene.time.now;
    for (const cfg of preset.types) {
      if (!scene.textures.exists(cfg.textureKey)) {
        console.warn(`[Critters] Pominięto typ — brak tekstury ${cfg.textureKey}`);
        continue;
      }
      this.types.push({
        cfg,
        // Pierwszy spawn z losowym jitterem (różne typy nie ruszają razem),
        // szybciej niż pełny interval żeby scena od razu trochę żyła.
        nextSpawnAt: now + Phaser.Math.Between(200, cfg.spawnIntervalMs.min),
        alive: [],
      });
    }
  }

  /**
   * Per-frame tick. `dt` w sekundach, `time` = scene.time.now (ms).
   * `playerX/Y` (world coords, opcjonalne) — przekazywane stworzonkom z
   * `cfg.flee`, żeby uciekały przed graczem.
   */
  update(time: number, dt: number, playerX?: number, playerY?: number): void {
    if (this.types.length === 0) return;
    const view = this.scene.cameras.main.worldView;

    for (const t of this.types) {
      // Spawn gdy czas + jest miejsce.
      if (time >= t.nextSpawnAt && t.alive.length < t.cfg.maxConcurrent) {
        t.alive.push(new AmbientCritter(this.scene, { cfg: t.cfg, view }));
        t.nextSpawnAt = time + Phaser.Math.Between(
          t.cfg.spawnIntervalMs.min,
          t.cfg.spawnIntervalMs.max,
        );
      }

      // Tick + zbieranie zakończonych (iteracja od tyłu — bezpieczne usuwanie).
      for (let i = t.alive.length - 1; i >= 0; i--) {
        const critter = t.alive[i]!;
        if (critter.update(dt, time, playerX, playerY)) {
          critter.destroy();
          t.alive.splice(i, 1);
        }
      }
    }
  }

  /** Niszczy wszystkie żywe stworzonka. Idempotentny. */
  destroy(): void {
    for (const t of this.types) {
      for (const critter of t.alive) critter.destroy();
      t.alive.length = 0;
    }
    this.types.length = 0;
  }
}
